import React, { useEffect } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import SecondaryBanner from '@ajat/design-system/ContentBlocks/SecondaryBanner';
import ProductsGrid from '@ajat/design-system/ContentBlocks/ProductsGrid';
import Preloader from '@ajat/design-system/Preloader';
import { usePageLoader } from '@ajat/design-system/PageLoader';
import ShopEmptyState from '../SchoolShop/ShopEmptyState';
import useCollectionListing from '../../../../hooks/useCollectionListing';
import productShortDescription from '../../../../utils/shortDescription';
import '../SchoolShop/SchoolShop.css';
import { discountCardFields } from '../../../../services/discountBadge';

/**
 * CollectionShop — the public "collection shop" listing page
 * (route /clothing/{feSlug}/collection/:collectionSlug).
 *
 * A rep-distributed price-tier link: shows ONE collection's product
 * variations (tier prices, pinned colors) + packages, with NO school context
 * — no selector gating, no localStorage/cart-school writes. Deliberately not
 * gated on the brand's open-shop flag (these are special links a sales rep
 * hands out, like class-shop links). Product cards open the bare PDP with
 * ?variation={itemId}&collection={slug}, which makes the PDP render the
 * variation AND stamps the cart line as collection-link origin — checkout
 * then requires picking school + class (rep checkbox appears when the picked
 * school has a rep).
 *
 * Shared across brands; each brand mounts it with its own Header/Footer and
 * copy (see components/Clothing/{BRAND}/CollectionShop).
 */
const CollectionShop = ({
  brandSlug,
  feSlug,
  dataBrand,
  HeaderComponent,
  FooterComponent,
  packageDescription = '',
  loadingText = 'Loading…',
  emptyTitle = 'No products right now',
  emptyText = 'No products available right now.',
  contactLabel = 'Contact us',
  faqLabel = 'See FAQ',
}) => {
  const navigate = useNavigate();
  const { collectionSlug } = useParams();

  const { collection, variations, packages, loading, error } = useCollectionListing(brandSlug, collectionSlug);
  // ABC-SE/CLS-SE/CLS-FI have no Trustpilot profile of their own — the DS default is CLS Denmark's.
  const hideTrustpilot = brandSlug === 'abcse' || brandSlug === 'clsse' || brandSlug === 'clsfi';
  usePageLoader(loading, { brand: dataBrand, label: loadingText });

  /* Set global brand on mount, restore previous on unmount */
  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-brand');
    document.documentElement.setAttribute('data-brand', dataBrand);
    return () => {
      if (prev) document.documentElement.setAttribute('data-brand', prev);
      else document.documentElement.removeAttribute('data-brand');
    };
  }, [dataBrand]);

  // Unknown/renamed/inactive collection slug (404) → brand landing.
  if (!loading && error) {
    return <Navigate to={`/clothing/${feSlug}`} replace />;
  }

  const productItems = variations
    .filter((v) => v.slug)
    .map((v) => ({
      id: v.id,
      image: v.images?.[0]?.src || undefined,
      imageAlt: v.name,
      title: v.name,
      description: productShortDescription(v),
      price: v.price,
      ...discountCardFields(v, feSlug),
      onClick: () => navigate(
        `/clothing/${feSlug}/product-details/${encodeURIComponent(v.slug)}`
        + `?variation=${encodeURIComponent(v.id)}&collection=${encodeURIComponent(collectionSlug)}`,
      ),
    }));

  const packageItems = packages
    .filter((p) => p.slug)
    .map((p) => ({
      id: `pkg-${p.id}`,
      image: p.image || undefined,
      imageAlt: p.name,
      title: p.name,
      description: productShortDescription(p) ?? packageDescription ?? undefined,
      price: p.price,
      onClick: () => navigate(`/clothing/${feSlug}/product-package/${encodeURIComponent(p.slug)}`),
    }));

  const isEmpty = productItems.length === 0 && packageItems.length === 0;

  return (
    <div className="school-shop">
      <HeaderComponent />

      <SecondaryBanner tag="" title={collection?.name || ''} trustpilot={hideTrustpilot ? null : undefined} />

      <main className="school-shop__wrapper">
        {(loading || isEmpty) ? (
          loading ? (
            <div style={{ padding: '4rem 2rem', textAlign: 'center' }}>
              <Preloader label={loadingText} />
            </div>
          ) : (
            <ShopEmptyState
              feSlug={feSlug}
              title={emptyTitle}
              text={emptyText}
              contactLabel={contactLabel}
              faqLabel={faqLabel}
            />
          )
        ) : (
          <>
            {productItems.length > 0 && (
              <ProductsGrid
                paddingTop="medium-l"
                paddingBottom="medium-s"
                background="var(--color-gray-0)"
                items={productItems}
                centerIncomplete
              />
            )}
            {packageItems.length > 0 && (
              <ProductsGrid
                background="var(--color-gray-0)"
                paddingTop="medium-m"
                paddingBottom="large-l"
                items={packageItems}
                centerIncomplete
              />
            )}
          </>
        )}
      </main>

      <FooterComponent />
    </div>
  );
};

export default CollectionShop;
